import React from 'react';
import retoast from 'retoast';
import Spinner from 'react-bootstrap/Spinner';
import Icon from '../Icon';
import ProgressBar from 'react-bootstrap/ProgressBar';
import 'retoast/dist/css/main.css';
import './index.scss';
import branding from '@config/branding';

const soundEffectIn = () => {
  if (branding.toaster.soundEffectEnable) {
    const sound = new Audio(branding.toaster.pathSoundEffectIn);
    sound.play();
  }
};

const soundEffectDismissed = () => {
  if (branding.toaster.soundEffectEnable) {
    const sound = new Audio(branding.toaster.pathSoundEffectOut);
    sound.play();
  }
};

const Toaster = (message, {
  duration = 10000,
  placement = 'top-right'
} = {}) => {
  const randomKey = `${Math.random()}`;
  const reloader = /*#__PURE__*/React.createElement("div", {
    className: "toaster-content"
  }, /*#__PURE__*/React.createElement(Spinner, {
    animation: "border",
    size: "sm"
  }), /*#__PURE__*/React.createElement("div", {
    className: "toaster-message"
  }, message));
  retoast({
    key: randomKey,
    body: reloader,
    variant: 'info',
    duration: 0,
    placement,
    onMount: soundEffectIn
  });
  return {
    void: () => {
      retoast({
        key: randomKey,
        body: reloader,
        duration: 1,
        placement,
        variant: 'info'
      });
    },
    success: message => {
      retoast({
        key: randomKey,
        body: reloader,
        duration: 1,
        placement,
        variant: 'info',
        onUnmount: () => {
          retoast({
            body: /*#__PURE__*/React.createElement("div", {
              className: "toaster-content"
            }, /*#__PURE__*/React.createElement(Icon, {
              name: "check"
            }), /*#__PURE__*/React.createElement("div", {
              className: "toaster-message"
            }, message)),
            variant: 'success',
            duration,
            placement,
            onMount: soundEffectIn,
            onDismissed: soundEffectDismissed,
            dismissible: true
          });
        }
      });
    },
    fail: (message = /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: '14px'
      }
    }, branding.messages.fail)) => {
      retoast({
        key: randomKey,
        body: reloader,
        duration: 1,
        placement,
        variant: 'info',
        onUnmount: () => {
          retoast({
            body: /*#__PURE__*/React.createElement("div", {
              className: "toaster-content"
            }, /*#__PURE__*/React.createElement(Icon, {
              name: "exclamation-triangle"
            }), /*#__PURE__*/React.createElement("div", {
              className: "toaster-message"
            }, message)),
            variant: 'warning',
            duration,
            placement,
            onMount: soundEffectIn,
            onDismissed: soundEffectDismissed,
            dismissible: true
          });
        }
      });
    },
    error: (message = branding.messages.error) => {
      retoast({
        key: randomKey,
        body: reloader,
        duration: 1,
        placement,
        variant: 'info',
        onUnmount: () => {
          retoast({
            body: /*#__PURE__*/React.createElement("div", {
              className: "toaster-content"
            }, /*#__PURE__*/React.createElement(Icon, {
              name: "exclamation-circle"
            }), /*#__PURE__*/React.createElement("div", {
              className: "toaster-message"
            }, message)),
            variant: 'danger',
            duration,
            placement,
            onMount: soundEffectIn,
            onDismissed: soundEffectDismissed,
            dismissible: true
          });
        }
      });
    }
  };
};

Toaster.success = (message, {
  duration = 10000,
  placement = 'top-right'
} = {}) => {
  retoast({
    body: /*#__PURE__*/React.createElement("div", {
      className: "toaster-content"
    }, /*#__PURE__*/React.createElement(Icon, {
      name: "check"
    }), /*#__PURE__*/React.createElement("div", {
      className: "toaster-message"
    }, message)),
    variant: 'success',
    duration,
    placement,
    onMount: soundEffectIn,
    onDismissed: soundEffectDismissed,
    dismissible: true
  });
};

Toaster.fail = (message = /*#__PURE__*/React.createElement("span", {
  style: {
    fontSize: '14px'
  }
}, branding.messages.fail), {
  duration = 10000,
  placement = 'top-right'
} = {}) => {
  retoast({
    body: /*#__PURE__*/React.createElement("div", {
      className: "toaster-content"
    }, /*#__PURE__*/React.createElement(Icon, {
      name: "exclamation-triangle"
    }), /*#__PURE__*/React.createElement("div", {
      className: "toaster-message"
    }, message)),
    variant: 'warning',
    duration,
    placement,
    onMount: soundEffectIn,
    onDismissed: soundEffectDismissed,
    dismissible: true
  });
};

Toaster.info = (message, {
  duration = 10000,
  placement = 'top-right'
} = {}) => {
  retoast({
    body: /*#__PURE__*/React.createElement("div", {
      className: "toaster-content"
    }, /*#__PURE__*/React.createElement(Icon, {
      name: "info-circle"
    }), /*#__PURE__*/React.createElement("div", {
      className: "toaster-message"
    }, message)),
    variant: 'info',
    duration,
    placement,
    onMount: soundEffectIn,
    onDismissed: soundEffectDismissed,
    dismissible: true
  });
};

Toaster.error = (message = branding.messages.error, {
  duration = 10000,
  placement = 'top-right'
} = {}) => {
  retoast({
    body: /*#__PURE__*/React.createElement("div", {
      className: "toaster-content"
    }, /*#__PURE__*/React.createElement(Icon, {
      name: "exclamation-circle"
    }), /*#__PURE__*/React.createElement("div", {
      className: "toaster-message"
    }, message)),
    variant: 'danger',
    duration,
    placement,
    onMount: soundEffectIn,
    onDismissed: soundEffectDismissed,
    dismissible: true
  });
};

Toaster.progress = ({
  progress,
  body,
  progressVariant,
  ...props
}) => {
  retoast({
    onMount: soundEffectIn,
    onDismissed: soundEffectDismissed,
    duration: 0,
    ...props,
    body: /*#__PURE__*/React.createElement("div", {
      className: "toaster-content toaster-progress"
    }, body, /*#__PURE__*/React.createElement(ProgressBar, {
      variant: progressVariant,
      animated: true,
      label: `${progress}%`,
      now: progress
    }))
  });
};

Toaster.retoast = props => {
  retoast({
    onMount: soundEffectIn,
    onDismissed: soundEffectDismissed,
    ...props
  });
};

export default Toaster;